import { CountMetric, EnvironmentDetail } from '@amzn/ihm-associate-tools-js-lib'
import { CandyCaneBluetoothErrorMappings } from './CandyCaneBluetoothErrorMappings'

export const BluetoothPairedDeviceConstants = Object.freeze({
    LAST_BLUETOOTH_PAIRED_DEVICES: 'lastBluetoothPairedDevices',
    DEFAULT_DEVICE_TYPE: 'PRINTER',
})

export class BluetoothPairedDevicesService {

    // Public Functions

    /**
     * API to get last paired device with the current device.
     * @param deviceType - The type of device - PRINTER, HEADSET etc. Defaults to PRINTER.
     * @return {*} A promise with last paired device of given device type.
     */
    public static getLastPairedDevice (deviceType) {
        const getLastPairedDeviceFaultMetrics = new CountMetric(undefined, 'Fault', 'IhmFlyingDutchman', 'bluetoothPairedDevicesService.getLastBluetoothPairedDevice')
        try {
            if (!deviceType) {
                console.error('No deviceType provided in the request.')
                return Promise.reject('deviceType cannot be empty')
            }

            const pairedDevices = this.getPairedDevicesFromLocalStorage()
            const lastPairedDevice = pairedDevices[deviceType]

            if (!lastPairedDevice) {
                console.info('No Paired devices of deviceType: ' + deviceType + ' found for this device.')
                return Promise.reject('No Paired devices of deviceType: ' + deviceType + ' found for this device.')
            }
            console.info('ATBase BluetoothPairedDevicesService getLastPairedDevice: ' + deviceType + ' lastPairedDevice:' + lastPairedDevice)
            getLastPairedDeviceFaultMetrics.count(0)
            return Promise.resolve(lastPairedDevice)
        } catch (error) {
            getLastPairedDeviceFaultMetrics.count(1)
            console.error('Unexpected error in retrieving paired device info for the deviceType: ' + deviceType + '. The error is ' + error)
            return Promise.reject('Unexpected error in retrieving paired device info for the deviceType: ' + deviceType)
        }
    }
    /**
     * API to set last paired device with the current device.
     * @param deviceType - Optional Param - The type of device - PRINTER, HEADSET etc. Defaults to PRINTER.
     * @param device - MAC Address of last paired device.
     */

    public static setLastPairedDevice (deviceType, device) {
        const setLastPairedDeviceFaultMetrics = new CountMetric(undefined, 'Fault', 'IhmFlyingDutchman', 'bluetoothPairedDevicesService.setLastBluetoothPairedDevice')
        try {
            if (!deviceType) {
                console.error('Bluetooth No deviceType provided in the request.')
                return Promise.reject({
                    errorMessage: 'deviceType cannot be empty',
                    errorCode: 8,
                })
            }

            if (!device) {
                console.error('Bluetooth No device mac Address provided.')
                return Promise.reject({
                    errorMessage: 'Device Mac Address not provided.',
                    errorCode: 8,
                })
            }
            console.info('ATBase BluetoothPairedDevicesService setLastPairedDevice: ' + deviceType + ' device:' + device)
            const pairedDevices = this.getPairedDevicesFromLocalStorage()
            pairedDevices[deviceType] = device
            this.setLastPairedDeviceToLocalStorage(pairedDevices)
            setLastPairedDeviceFaultMetrics.count(0)
            return Promise.resolve(true)
        } catch (error) {
            setLastPairedDeviceFaultMetrics.count(1)
            console.log('Bluetooth Error in setting paired device info for deviceType: ' + deviceType + '. The error is ' + error)
            return Promise.reject({
                errorMessage: 'Paired devices not set in local storage due to ' + error,
            })
        }
    }

    /**
     * Deletes the lastBluetoothPairedDevice for a given deviceType from localStorage
     * @param deviceType deviceType to be deleted.
     * @return {*} A promise.
     */
    public static clearLastPairedDevice (deviceType) {
        const clearLastPairedDeviceFaultMetrics = new CountMetric(undefined, 'Fault', 'IhmFlyingDutchman', 'bluetoothPairedDevicesService.clearLastBluetoothPairedDevice')
        try {

            if (!deviceType) {
                console.error('No deviceType provided in the request.')
                return Promise.reject('deviceType cannot be empty')
            }

            const pairedDevices = this.getPairedDevicesFromLocalStorage()
            delete pairedDevices[deviceType]

            this.setLastPairedDeviceToLocalStorage(pairedDevices)
            clearLastPairedDeviceFaultMetrics.count(0)
            return Promise.resolve(true)
        } catch (error) {
            clearLastPairedDeviceFaultMetrics.count(1)
            console.error('Error in deleting paired device info for deviceType: ' + deviceType + '. The error is ' + error)
            return Promise.reject('Paired device not deleted from local storage')
        }
    }

    /**
     * Removes the entry for storing last bluetooth paired devices from local storage.
     * This removes all last paired devices for all device types.
     */
    public static clearAllLastPairedDevices () {
        console.info('Removing all last bluetooth paired devices from local storage.')
        window.localStorage.removeItem(BluetoothPairedDeviceConstants.LAST_BLUETOOTH_PAIRED_DEVICES)
    }

    /**
     * Helper function to get the last paired devices from localStorage.
     * @return {string} JSON object of last paired devices.
     */
    public static getPairedDevicesFromLocalStorage () {
        const pairedDevices = window.localStorage.getItem(BluetoothPairedDeviceConstants.LAST_BLUETOOTH_PAIRED_DEVICES)
        return pairedDevices ? JSON.parse(pairedDevices) : {}
    }

    /**
     * Helper function to store the last paired devices in localStorage.
     * Example: {'lastBluetoothPairedDevices': {'PRINTER': 'MAC_ADDRESS1', 'HEADSET': 'MAC_ADDRESS2}}
     */
    public static setLastPairedDeviceToLocalStorage (pairedDevices) {
        const pairedDevicesJson = JSON.stringify(pairedDevices)
        window.localStorage.setItem(BluetoothPairedDeviceConstants.LAST_BLUETOOTH_PAIRED_DEVICES, pairedDevicesJson)
    }
}
